import { Link, createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { usePayments } from '@/api/queries';
import type { PaymentStatus, PspCode } from '@/api/types';
import { Empty, ErrorBox } from '@/components/empty';
import { PaymentStatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatLyd } from '@/lib/utils';

const filterSchema = z.object({
  psp: z.enum(['sadad', 'moamalat', 'mobicash', 'tlync', 'adfali']).optional(),
  status: z
    .enum(['pending', 'requires_action', 'processing', 'succeeded', 'failed', 'expired', 'refunded'])
    .optional(),
});

export const Route = createFileRoute('/_authed/payments/')({
  validateSearch: (raw) => filterSchema.parse(raw),
  component: PaymentsList,
});

const PSPS: PspCode[] = ['sadad', 'moamalat', 'mobicash', 'tlync', 'adfali'];
const STATUSES: PaymentStatus[] = [
  'pending',
  'requires_action',
  'processing',
  'succeeded',
  'failed',
  'expired',
  'refunded',
];

function PaymentsList() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [page, setPage] = useState(0);
  const limit = 50;

  const payments = usePayments({
    psp: search.psp,
    status: search.status,
    limit,
    offset: page * limit,
  });

  const setFilter = <K extends keyof typeof search>(key: K, value: typeof search[K] | undefined) => {
    setPage(0);
    navigate({ search: { ...search, [key]: value } });
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('payments.title')}</h1>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <FilterSelect
            label={t('payments.filter_psp')}
            value={search.psp}
            options={PSPS}
            onChange={(v) => setFilter('psp', v as PspCode | undefined)}
          />
          <FilterSelect
            label={t('payments.filter_status')}
            value={search.status}
            options={STATUSES}
            onChange={(v) => setFilter('status', v as PaymentStatus | undefined)}
          />
        </div>
      </header>

      {payments.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('payments.cols.created')}</TableHead>
              <TableHead>{t('payments.cols.order_ref')}</TableHead>
              <TableHead>{t('payments.cols.psp')}</TableHead>
              <TableHead>{t('payments.cols.amount')}</TableHead>
              <TableHead>{t('payments.cols.status')}</TableHead>
              <TableHead>{t('payments.cols.psp_ref')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.data?.map((p) => (
              <TableRow key={p.id} className="cursor-pointer">
                <TableCell className="font-mono text-xs whitespace-nowrap">
                  {dayjs(p.created_at).format('MMM D HH:mm:ss')}
                </TableCell>
                <TableCell className="font-medium">
                  <Link
                    to="/payments/$paymentId"
                    params={{ paymentId: p.id }}
                    className="hover:underline"
                  >
                    {p.merchant_order_ref}
                  </Link>
                </TableCell>
                <TableCell className="capitalize">{p.psp_code}</TableCell>
                <TableCell className="tabular-nums">{formatLyd(p.amount_minor)}</TableCell>
                <TableCell>
                  <PaymentStatusBadge status={p.status} />
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {p.psp_ref ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!payments.isLoading && payments.data?.length === 0 && (
          <Empty title={t('payments.no_results')} className="border-0" />
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
          {t('common.back')}
        </Button>
        <span className="text-xs text-muted-foreground">{page + 1}</span>
        <Button
          variant="outline"
          size="sm"
          disabled={(payments.data?.length ?? 0) < limit}
          onClick={() => setPage((p) => p + 1)}
        >
          ›
        </Button>
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | undefined;
  options: readonly string[];
  onChange: (v: string | undefined) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 min-w-[140px] flex-1 sm:flex-none">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <Select value={value ?? '__all'} onValueChange={(v) => onChange(v === '__all' ? undefined : v)}>
        <SelectTrigger className="w-full sm:w-40">
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all">{t('common.all')}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
